import { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'config/redux'
import { Reservations } from 'constants/interfaces'
import keys from 'constants/keys'
import { updateResults } from 'redux/actions/tasks_v2'
import { useTaskOverviewContext } from '../TaskOverviewStackProvider'

interface SkipSubtaskOptions {
  tid: string
  st_id: string
  subtaskType: number
  resultContextId: string
  unavailable: boolean
}

/** A skip is a result mutation: keep the reason draft open until this occurrence is confirmed. */
export function useSkipSubtask({ tid, st_id, subtaskType, resultContextId, unavailable }: SkipSubtaskOptions) {
  const dispatch: AppDispatch = useDispatch()
  const { task, isReadMode, isSubtaskBusy, setIsSubtaskBusy } = useTaskOverviewContext()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasError, setHasError] = useState(false)
  const submittingRef = useRef(false)
  const disabled = unavailable || isReadMode || isSubtaskBusy

  const open = () => {
    if (disabled || submittingRef.current) return
    setHasError(false)
    setIsModalOpen(true)
  }

  const close = () => {
    if (submittingRef.current) return
    setIsModalOpen(false)
    setHasError(false)
  }

  const confirm = async (reservations: Reservations) => {
    if (disabled || submittingRef.current) return
    submittingRef.current = true
    setIsSubmitting(true)
    setIsSubtaskBusy(true)
    setHasError(false)

    try {
      const saved = await dispatch(
        updateResults({
          tid,
          st_id,
          type: subtaskType,
          resultContextId,
          recurrence_ts: task.recurrence_ts ?? null,
          status: keys.TASK_STATUSES.SKIPPED.value,
          reservations,
          optimistic: false,
        })
      )
      if (saved) setIsModalOpen(false)
      else setHasError(true)
    } catch {
      setHasError(true)
    } finally {
      submittingRef.current = false
      setIsSubmitting(false)
      setIsSubtaskBusy(false)
    }
  }

  return { isModalOpen, isSubmitting, hasError, disabled, isReadMode, open, close, confirm }
}
